/**
 * @file fonts.ts
 * @description Каталог шрифтов платформы для оформления сайта (phase19.16 Р1;
 * SB10 — три роли, только каталог, кириллица и свободная лицензия, файлы с
 * нашего сервера; db-design-r1 §2 спорное место 7 — реестр в коде, файлы в
 * образе web).
 *
 * Здесь только реестр и стек `font-family`. Файлы woff2 раздаёт apps/web
 * со своего сервера (ФЗ-152: без обращения к Google), путь строится по ключу:
 * ключ совпадает с идентификатором пакета `@fontsource/<key>`, поэтому web
 * подключает файлы без отдельной таблицы соответствий.
 *
 * В оформлении хранится только ключ (`SiteTheme.fonts`). Неизвестный ключ
 * при чтении заменяется на `system` (readSiteTheme). Шрифт, который платформа
 * перестала предлагать, не удаляется из реестра, а помечается `retired`:
 * у магазинов он остаётся, пока владелец его не сменит (uiux-design-r1 §1,
 * состояние «Шрифт снят из каталога»).
 *
 * Состав: шрифты Google Fonts, у которых кириллица есть в самом семействе.
 * Сомнительные по кириллице или лицензии не включены (Lato, Poppins, Work
 * Sans — без кириллицы; Roboto Slab, Arimo, Tinos — Apache-2.0, Ubuntu — своя
 * лицензия; их можно добавить после сверки). Marianna — только после ответа
 * по лицензии (SB84, OQ-125); Mr Bedfort не переносится. Лицензию каждого
 * добавляемого шрифта сверяет человек — это данные каталога, а не
 * автоматическая проверка.
 *
 * ── Свои шрифты магазина (SB99, фаза §7.12, шаг Р1-7) ──
 * Ключ `shop:<id>` (id — cuid записи шрифта магазина; задел db-design-r1 §2
 * п.7). Синтаксис ключа проверяет этот файл, существование и принадлежность
 * магазину — сервер. Записи своих шрифтов приходят из данных магазина,
 * поэтому каталог платформы и свои шрифты объединяет реестр
 * (`createSiteFontRegistry`): платформенные записи в нём всегда, свои —
 * переданные вызывающим. В CSS свой шрифт попадает только под техническим
 * именем `cf-<id>` (`ShopFontEntry.cssFamily`): имя, которое ввёл магазин,
 * в CSS не пишется — нельзя внедрить правило через имя и нельзя случайно
 * совпасть с семейством каталога.
 */

/** Категория шрифта — фильтр в окне каталога (uiux-design-r1 §1). */
export type SiteFontCategory = "serif" | "sans" | "script" | "display";

export const SITE_FONT_CATEGORY_LABELS: Readonly<Record<SiteFontCategory, string>> = {
  serif: "С засечками",
  sans: "Без засечек",
  script: "Рукописные",
  display: "Декоративные",
};

/** Лицензия файлов шрифта. `system` — файлов нет, шрифт берётся из системы. */
export type SiteFontLicense = "OFL-1.1" | "system";

/** Запись каталога. */
export interface SiteFontEntry {
  /** Ключ в оформлении = id пакета `@fontsource/<key>`. Не меняется. */
  readonly key: string;
  /** Имя семейства в `font-family` и `@font-face`. */
  readonly family: string;
  readonly category: SiteFontCategory;
  /** Начертания, которые раздаёт web; в кабинете — только эти (uiux §1). */
  readonly weights: readonly number[];
  /** Всегда true: без кириллицы в каталог не попадает (SB10). */
  readonly cyrillic: true;
  readonly license: SiteFontLicense;
  /** Шрифт больше не предлагается новым магазинам, но у старых работает. */
  readonly retired?: boolean;
}

/** Ключ системного шрифта — умолчание платформы (SB4). */
export const SITE_SYSTEM_FONT_KEY = "system";

const SANS_FALLBACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';
const SERIF_FALLBACK = 'Georgia, "Times New Roman", "Noto Serif", serif';
// Рукописному и декоративному запасной — шрифт без засечек: системный cursive
// на Windows и Android без кириллицы даёт «квадратики» или Comic Sans.
const FALLBACK_BY_CATEGORY: Readonly<Record<SiteFontCategory, string>> = {
  sans: SANS_FALLBACK,
  serif: SERIF_FALLBACK,
  script: SANS_FALLBACK,
  display: SANS_FALLBACK,
};

const font = (
  key: string,
  family: string,
  category: SiteFontCategory,
  weights: readonly number[],
): SiteFontEntry => ({ key, family, category, weights, cyrillic: true, license: "OFL-1.1" });

const W_300_800 = [300, 400, 500, 600, 700, 800] as const;
const W_300_700 = [300, 400, 500, 600, 700] as const;
const W_400_800 = [400, 500, 600, 700, 800] as const;
const W_400_700 = [400, 500, 600, 700] as const;
const REGULAR = [400] as const;
const REGULAR_BOLD = [400, 700] as const;

/**
 * Реестр шрифтов. Порядок — порядок показа внутри категории. Начертания —
 * поднабор тех, что есть у семейства: раздавать все 9 весов незачем.
 */
export const SITE_FONTS: readonly SiteFontEntry[] = [
  {
    key: SITE_SYSTEM_FONT_KEY,
    family: "Системный",
    category: "sans",
    weights: REGULAR_BOLD,
    cyrillic: true,
    license: "system",
  },
  // ── Без засечек ──
  font("inter", "Inter", "sans", W_300_800),
  font("manrope", "Manrope", "sans", W_300_800),
  font("montserrat", "Montserrat", "sans", W_300_800),
  font("roboto", "Roboto", "sans", [300, 400, 500, 700]),
  font("open-sans", "Open Sans", "sans", W_300_800),
  font("pt-sans", "PT Sans", "sans", REGULAR_BOLD),
  font("noto-sans", "Noto Sans", "sans", W_300_800),
  font("source-sans-3", "Source Sans 3", "sans", W_300_800),
  font("nunito", "Nunito", "sans", W_300_800),
  font("raleway", "Raleway", "sans", W_300_800),
  font("rubik", "Rubik", "sans", W_300_800),
  font("jost", "Jost", "sans", W_300_800),
  font("golos-text", "Golos Text", "sans", W_400_800),
  font("onest", "Onest", "sans", W_300_800),
  font("fira-sans", "Fira Sans", "sans", W_300_800),
  font("ibm-plex-sans", "IBM Plex Sans", "sans", W_300_700),
  font("exo-2", "Exo 2", "sans", W_300_800),
  font("commissioner", "Commissioner", "sans", W_300_800),
  font("philosopher", "Philosopher", "sans", REGULAR_BOLD),
  font("tenor-sans", "Tenor Sans", "sans", REGULAR),
  // ── С засечками ──
  font("pt-serif", "PT Serif", "serif", REGULAR_BOLD),
  font("lora", "Lora", "serif", W_400_700),
  font("literata", "Literata", "serif", W_300_800),
  font("playfair-display", "Playfair Display", "serif", W_400_800),
  font("cormorant-garamond", "Cormorant Garamond", "serif", W_300_700),
  font("cormorant", "Cormorant", "serif", W_300_700),
  font("merriweather", "Merriweather", "serif", [300, 400, 700]),
  font("noto-serif", "Noto Serif", "serif", W_300_800),
  font("source-serif-4", "Source Serif 4", "serif", W_300_800),
  font("ibm-plex-serif", "IBM Plex Serif", "serif", W_300_700),
  font("eb-garamond", "EB Garamond", "serif", W_400_800),
  font("spectral", "Spectral", "serif", W_300_800),
  font("alegreya", "Alegreya", "serif", W_400_800),
  font("old-standard-tt", "Old Standard TT", "serif", REGULAR_BOLD),
  font("vollkorn", "Vollkorn", "serif", W_400_800),
  font("alice", "Alice", "serif", REGULAR),
  font("prata", "Prata", "serif", REGULAR),
  // ── Декоративные ──
  font("oswald", "Oswald", "display", W_300_700),
  font("unbounded", "Unbounded", "display", W_300_800),
  font("comfortaa", "Comfortaa", "display", W_300_700),
  font("russo-one", "Russo One", "display", REGULAR),
  font("yeseva-one", "Yeseva One", "display", REGULAR),
  font("forum", "Forum", "display", REGULAR),
  // ── Рукописные ──
  font("marck-script", "Marck Script", "script", REGULAR),
  font("caveat", "Caveat", "script", W_400_700),
  font("bad-script", "Bad Script", "script", REGULAR),
  font("pacifico", "Pacifico", "script", REGULAR),
  font("lobster", "Lobster", "script", REGULAR),
  font("amatic-sc", "Amatic SC", "script", REGULAR_BOLD),
  font("great-vibes", "Great Vibes", "script", REGULAR),
  font("neucha", "Neucha", "script", REGULAR),
];

const FONT_BY_KEY: ReadonlyMap<string, SiteFontEntry> = new Map(
  SITE_FONTS.map((entry) => [entry.key, entry]),
);

/** Ключи каталога (для zod-схемы и проверок). */
export const SITE_FONT_KEYS: readonly string[] = SITE_FONTS.map((entry) => entry.key);

/** Есть ли ключ в каталоге (включая снятые с предложения). */
export function isSiteFontKey(key: string): boolean {
  return FONT_BY_KEY.has(key);
}

/** Запись каталога по ключу; `undefined` — ключа нет. */
export function findSiteFont(key: string): SiteFontEntry | undefined {
  return FONT_BY_KEY.get(key);
}

/** Шрифты для окна выбора: без снятых, по категории или все. */
export function listSelectableSiteFonts(category?: SiteFontCategory): SiteFontEntry[] {
  return SITE_FONTS.filter(
    (entry) => !entry.retired && (category === undefined || entry.category === category),
  );
}

// ── Свои шрифты магазина (SB99, §7.12) ──

/** Префикс ключа своего шрифта магазина в оформлении. */
export const SHOP_FONT_KEY_PREFIX = "shop:";

/** id своего шрифта — cuid (`c` + 20..32 символа [a-z0-9]), как id записей Prisma. */
const SHOP_FONT_ID_PATTERN = /^c[a-z0-9]{20,32}$/;

/** Ключ оформления для своего шрифта: `shop:<id>`. */
export function shopFontKey(id: string): string {
  return `${SHOP_FONT_KEY_PREFIX}${id}`;
}

/**
 * id своего шрифта из ключа `shop:<cuid>`; `null` — не ключ своего шрифта
 * (включая ключ с префиксом, но с id не в формате cuid). Проверка только
 * синтаксиса: есть ли такой шрифт у магазина, решает сервер.
 */
export function parseShopFontKey(key: string): string | null {
  if (!key.startsWith(SHOP_FONT_KEY_PREFIX)) return null;
  const id = key.slice(SHOP_FONT_KEY_PREFIX.length);
  return SHOP_FONT_ID_PATTERN.test(id) ? id : null;
}

/** Ключ в формате своего шрифта магазина (`shop:<cuid>`). */
export function isShopFontKey(key: string): boolean {
  return parseShopFontKey(key) !== null;
}

/**
 * Техническое имя семейства своего шрифта в CSS: `cf-<id>`. Строится только
 * из проверенного cuid — в `font-family` не попадает ничего, что ввёл человек.
 */
export function shopFontCssFamily(id: string): string {
  return `cf-${id}`;
}

/** Начертание, которое магазин может загрузить: 2 веса × 2 стиля (SB99). */
export type ShopFontWeight = 400 | 700;
export type ShopFontStyle = "normal" | "italic";

/** Файл начертания своего шрифта (woff2 после перевода на сервере). */
export interface ShopFontFile {
  readonly weight: ShopFontWeight;
  readonly style: ShopFontStyle;
  /** Адрес файла, который строит сервер (раздача со своего домена, §7.11). */
  readonly url: string;
}

/** Свой шрифт магазина в реестре. */
export interface ShopFontEntry {
  /** `shop:<id>`. */
  readonly key: string;
  /** Имя для показа в кабинете — в CSS не попадает. */
  readonly family: string;
  /** `cf-<id>` — единственное имя семейства в CSS (защита от внедрения). */
  readonly cssFamily: string;
  /** Категория — для запасного стека и фильтра в окне выбора. */
  readonly category: SiteFontCategory;
  /** Есть ли кириллица в таблице символов (проверяет сервер при загрузке). */
  readonly hasCyrillic: boolean;
  readonly files: readonly ShopFontFile[];
}

/** Запись реестра: шрифт каталога платформы или свой шрифт магазина. */
export type AnySiteFontEntry = SiteFontEntry | ShopFontEntry;

/** Тип-гард: запись — свой шрифт магазина. */
export function isShopFontEntry(entry: AnySiteFontEntry): entry is ShopFontEntry {
  return "cssFamily" in entry && "files" in entry;
}

/**
 * Реестр шрифтов, доступных оформлению конкретного магазина: каталог
 * платформы + свои шрифты. Нужен везде, где ключ превращается в CSS или
 * проверяется при чтении (readSiteTheme, fontStack, сборка `@font-face`).
 */
export interface SiteFontRegistry {
  has(key: string): boolean;
  find(key: string): AnySiteFontEntry | undefined;
  /** Свои шрифты магазина (после проверки), в порядке передачи. */
  readonly shopFonts: readonly ShopFontEntry[];
}

const SHOP_FONT_WEIGHTS: ReadonlySet<number> = new Set([400, 700]);
const SHOP_FONT_STYLES: ReadonlySet<string> = new Set(["normal", "italic"]);
/**
 * Адрес файла попадает в `src:url("…")` инлайн-`<style>`: кавычки, скобки,
 * пробелы и обратная косая запрещены — строку из данных нельзя превратить
 * в CSS-правило, даже если запись реестра пришла не от нашего сервера.
 * Допустимы путь от корня (`/api/v1/...`) и абсолютный http(s)-адрес.
 */
const SHOP_FONT_URL_PATTERN = /^(?:https?:\/\/[A-Za-z0-9.-]+(?::\d+)?)?\/[A-Za-z0-9/._~%=&?+-]*$/;

/** Безопасен ли адрес файла шрифта (каталога или своего) для `src:url("…")`. */
export function isSafeFontFileUrl(url: string): boolean {
  return SHOP_FONT_URL_PATTERN.test(url);
}

/**
 * Приводит запись своего шрифта к безопасному виду: ключ — `shop:<cuid>`,
 * `cssFamily` — всегда `cf-<id>` (переданному значению не доверяем), файлы —
 * только допустимые веса/стили/адреса. Неверный ключ — запись отбрасывается.
 */
function normalizeShopFont(entry: ShopFontEntry): ShopFontEntry | null {
  const id = parseShopFontKey(entry.key);
  if (id === null) return null;
  const files = entry.files.filter(
    (file) =>
      SHOP_FONT_WEIGHTS.has(file.weight) &&
      SHOP_FONT_STYLES.has(file.style) &&
      isSafeFontFileUrl(file.url),
  );
  const category: SiteFontCategory = Object.hasOwn(FALLBACK_BY_CATEGORY, entry.category)
    ? entry.category
    : "sans";
  return { ...entry, cssFamily: shopFontCssFamily(id), category, files };
}

/**
 * Реестр шрифтов магазина. Платформенные записи внутри всегда; свои шрифты —
 * из аргумента (сервер берёт их из базы, витрина — из site-config). Ключ
 * своего шрифта не может совпасть с ключом каталога: у каталога нет `:`.
 */
export function createSiteFontRegistry(shopFonts: readonly ShopFontEntry[] = []): SiteFontRegistry {
  const own = new Map<string, ShopFontEntry>();
  for (const entry of shopFonts) {
    const normalized = normalizeShopFont(entry);
    // Дубликат ключа — выигрывает первая запись: порядок задаёт сервер.
    if (normalized && !own.has(normalized.key)) own.set(normalized.key, normalized);
  }
  const list: readonly ShopFontEntry[] = [...own.values()];
  return {
    has: (key) => FONT_BY_KEY.has(key) || own.has(key),
    find: (key) => FONT_BY_KEY.get(key) ?? own.get(key),
    shopFonts: list,
  };
}

/** Реестр без своих шрифтов — только каталог платформы (умолчание всех функций). */
export const PLATFORM_FONT_REGISTRY: SiteFontRegistry = createSiteFontRegistry();

/** Шрифты оформления по ролям — минимум, который нужен `themeShopFontKeys`. */
interface ThemeFontsLike {
  readonly fonts: { readonly heading: string; readonly body: string; readonly accent: string | null };
}

/** Уникальные ключи своих шрифтов, которые использует оформление (в порядке ролей). */
export function themeShopFontKeys(theme: ThemeFontsLike): string[] {
  const keys: string[] = [];
  for (const key of [theme.fonts.heading, theme.fonts.body, theme.fonts.accent]) {
    if (key !== null && isShopFontKey(key) && !keys.includes(key)) keys.push(key);
  }
  return keys;
}

/** Веса, у которых есть загруженный файл своего шрифта (по возрастанию). */
function shopFontWeights(entry: ShopFontEntry): number[] {
  return [...new Set<number>(entry.files.map((file) => file.weight))].sort((a, b) => a - b);
}

/**
 * Стек `font-family` для CSS: семейство в кавычках + запасные по категории.
 * Неизвестный ключ и `system` — системный стек (умолчание платформы).
 * Свой шрифт — техническое имя `cf-<id>`: имя магазина в CSS не пишется.
 */
export function fontStack(key: string, registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY): string {
  const entry = registry.find(key);
  if (!entry) return SANS_FALLBACK;
  if (isShopFontEntry(entry)) return `"${entry.cssFamily}", ${FALLBACK_BY_CATEGORY[entry.category]}`;
  if (entry.license === "system") return SANS_FALLBACK;
  return `"${entry.family}", ${FALLBACK_BY_CATEGORY[entry.category]}`;
}

/**
 * Ближайшее начертание, которое есть у шрифта. Нужно для CSS: если владелец
 * выбрал 700, а у шрифта только 400, браузер нарисует «ложный жирный».
 * При равном расстоянии берётся более жирное (заголовок не должен бледнеть).
 * У своего шрифта — из загруженных весов (400/700); файлов нет — 400.
 */
export function nearestFontWeight(
  key: string,
  weight: number,
  registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): number {
  const entry = registry.find(key);
  let weights: readonly number[];
  if (entry !== undefined && isShopFontEntry(entry)) {
    const loaded = shopFontWeights(entry);
    weights = loaded.length > 0 ? loaded : REGULAR;
  } else {
    weights = entry?.weights ?? REGULAR_BOLD;
  }
  let best = weights[0] ?? 400;
  for (const w of weights) {
    const d = Math.abs(w - weight);
    const bestD = Math.abs(best - weight);
    if (d < bestD || (d === bestD && w > best)) best = w;
  }
  return best;
}
